import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FormikProvider, useFormik } from 'formik';
import { NumericFormat } from 'react-number-format';
import { Loader } from 'app/shared/loader/loader';
import { Button, Input, Modal } from 'edozvola-frontend-lib/dist/ui/components';
import { Label, TextArea } from 'edozvola-frontend-lib/dist/ui';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import {
  specialBuildingPartInitialData,
  SpecialBuildingPartModel,
} from 'app/edozvola/cases/special-building-part/model/special-building-part.model';
import { specialBuildingPartValidationSchema } from 'app/edozvola/cases/special-building-part/model/special-building-part.schema';
import {
  existsSpecialBuildingPartMutation,
  fetchCaseBuildingOptionsQuery,
  fetchSpecialBuildingPartQuery,
  saveSpecialBuildingPartQuery,
} from 'app/edozvola/cases/special-building-part/service/special-building-part.queries';
import {
  fetchCmrBathroomCountCodebookQuery,
  fetchCmrBuildingPartCodebookQuery,
  fetchCmrCoolingConnectionCodebookQuery,
  fetchCmrDisabilityAdaptationCodebookQuery,
  fetchCmrElectricityConnectionCodebookQuery,
  fetchCmrElevatorAccessCodebookQuery,
  fetchCmrFloorCodebookQuery,
  fetchCmrFloorTypeCodebookQuery,
  fetchCmrGasConnectionCodebookQuery,
  fetchCmrHeatingConnectionCodebookQuery,
  fetchCmrHeatingEnergySourceCodebookQuery,
  fetchCmrInternetConnectionCodebookQuery,
  fetchCmrKitchenCountCodebookQuery,
  fetchCmrLivingRoomCountCodebookQuery,
  fetchCmrRoomCountCodebookQuery,
  fetchCmrSeparateToiletCountCodebookQuery,
  fetchCmrSewageConnectionCodebookQuery,
  fetchCmrSpaceTypeCodebookQuery,
  fetchCmrSpecialBuildingPartCodebookQuery,
  fetchCmrWaterSupplyConnectionCodebookQuery,
} from 'app/edozvola/codebooks/codebooks.queries';

export interface ISpecialBuildingPartFormProps {
  readOnly: boolean;
  setSpecialBuildingPartId: any;
  specialBuildingPartId: string | null;
}

const SpecialBuildingPartForm = (props: ISpecialBuildingPartFormProps) => {
  const { id: uuid } = useParams<{ id: string }>();
  const [notUnique, setNotUnique] = useState(false);

  const { data: specialBuildingPartData, isLoading: dataLoading } = fetchSpecialBuildingPartQuery(props.specialBuildingPartId);
  const { data: buildingOptions, isLoading: buildingOptionsLoading } = fetchCaseBuildingOptionsQuery(uuid);

  const codebooks = {
    buildingPart: fetchCmrBuildingPartCodebookQuery(),
    specialBuildingPart: fetchCmrSpecialBuildingPartCodebookQuery(),
    spaceType: fetchCmrSpaceTypeCodebookQuery(),
    floor: fetchCmrFloorCodebookQuery(),
    floorType: fetchCmrFloorTypeCodebookQuery(),
    roomCount: fetchCmrRoomCountCodebookQuery(),
    livingRoomCount: fetchCmrLivingRoomCountCodebookQuery(),
    kitchenCount: fetchCmrKitchenCountCodebookQuery(),
    bathroomCount: fetchCmrBathroomCountCodebookQuery(),
    separateToiletCount: fetchCmrSeparateToiletCountCodebookQuery(),
    disabilityAdaptation: fetchCmrDisabilityAdaptationCodebookQuery(),
    elevatorAccess: fetchCmrElevatorAccessCodebookQuery(),
    waterSupplyConnection: fetchCmrWaterSupplyConnectionCodebookQuery(),
    sewageConnection: fetchCmrSewageConnectionCodebookQuery(),
    electricityConnection: fetchCmrElectricityConnectionCodebookQuery(),
    gasConnection: fetchCmrGasConnectionCodebookQuery(),
    internetConnection: fetchCmrInternetConnectionCodebookQuery(),
    heatingConnection: fetchCmrHeatingConnectionCodebookQuery(),
    heatingEnergySource: fetchCmrHeatingEnergySourceCodebookQuery(),
    coolingConnection: fetchCmrCoolingConnectionCodebookQuery(),
  };

  const closeModal = () => {
    props.setSpecialBuildingPartId(null);
  };

  const formik = useFormik({
    initialValues: specialBuildingPartData || specialBuildingPartInitialData,
    validationSchema: specialBuildingPartValidationSchema,
    onSubmit: (values: SpecialBuildingPartModel) => saveMutation(values),
    enableReinitialize: true,
    validateOnMount: true,
  });

  const { mutate: saveMutation, isPending: savePending } = saveSpecialBuildingPartQuery(uuid, () => closeModal());

  /**
   * Sifrarnici za koje specifikacija propisuje inicijalnu vrijednost -
   * prvu vrijednost iz sifrarnika, tj. onu s najnizim redoslijedom.
   */
  const CODEBOOKS_WITH_DEFAULT_VALUE = [
    'spaceType',
    'livingRoomCount',
    'kitchenCount',
    'bathroomCount',
    'separateToiletCount',
    'disabilityAdaptation',
    'elevatorAccess',
    'waterSupplyConnection',
    'sewageConnection',
    'electricityConnection',
    'gasConnection',
    'internetConnection',
    'heatingConnection',
    'heatingEnergySource',
    'coolingConnection',
  ];

  const isNewSpecialBuildingPart = props.specialBuildingPartId === '';
  const codebooksLoading = CODEBOOKS_WITH_DEFAULT_VALUE.some(name => codebooks[name].isLoading);

  useEffect(() => {
    if (!isNewSpecialBuildingPart || codebooksLoading) {
      return;
    }

    const defaults = CODEBOOKS_WITH_DEFAULT_VALUE.reduce((values, name) => {
      const firstValue = codebooks[name].data?.[0];

      return firstValue ? { ...values, [name]: [firstValue] } : values;
    }, {});

    formik.setValues({ ...formik.values, ...defaults });
  }, [isNewSpecialBuildingPart, codebooksLoading]);

  const { mutate: checkUniqueness, isPending: checkingUniqueness } = existsSpecialBuildingPartMutation(setNotUnique);

  const validateUniqueness = (values: SpecialBuildingPartModel) => {
    const idBuilding = values.building?.[0]?.id;
    const idBuildingPart = values.buildingPart?.[0]?.id;
    const idSpecialBuildingPart = values.specialBuildingPart?.[0]?.id;

    if (!idBuilding || !idBuildingPart || !idSpecialBuildingPart) {
      setNotUnique(false);

      return;
    }

    checkUniqueness({
      idBuilding: Number(idBuilding),
      idBuildingPart: Number(idBuildingPart),
      idSpecialBuildingPart: Number(idSpecialBuildingPart),
      excludeId: props.specialBuildingPartId ? Number(props.specialBuildingPartId) : null,
    });
  };

  const onTripletFieldSelected = (name: string, selected) => {
    const values = { ...formik.values, [name]: selected };

    formik.setValues(values);
    validateUniqueness(values);
  };

  const onBuildingSelected = selected => {
    const values = {
      ...formik.values,
      building: selected,
      ibz: selected && selected[0] ? selected[0].ibz : '',
    };

    formik.setValues(values);
    validateUniqueness(values);
  };

  const codebookField = (name: string, labelText: string, onSelect?: (selected) => void, additionalError?: string) => {
    const { data, isLoading } = codebooks[name];
    const fieldError = formik.errors[name] ? `${formik.errors[name]}` : additionalError;

    return (
      <>
        <Label labelText={labelText} />
        <PaginatedDropdown
          placeholder={!props.readOnly && 'Odaberi vrijednost'}
          onItemSelect={selected => (onSelect ? onSelect(selected) : formik.setFieldValue(name, selected))}
          selected={formik.values[name]}
          labelKey={'value'}
          options={isLoading ? [] : data}
          disabled={props.readOnly}
          error={!props.readOnly && fieldError ? fieldError : ''}
          showErrorMessage
        />
      </>
    );
  };

  return (
    <>
      {!dataLoading && (
        <Modal scrollable isOpen={props.specialBuildingPartId !== null} size={'xl'}>
          {props.readOnly ? (
            <ModalHeader toggle={() => closeModal()}>Pregled posebnog dijela zgrade</ModalHeader>
          ) : (
            <ModalHeader>
              {props.specialBuildingPartId === '' ? 'Unos posebnog dijela zgrade' : 'Uređivanje posebnog dijela zgrade'}
            </ModalHeader>
          )}
          <ModalBody>
            <FormikProvider value={formik}>
              {savePending && <Loader />}
              <div className={'d-flex justify-content-between flex-column gap-3'}>
                <div className={'form-header'}>Podaci o zgradi</div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Zgrada'} />
                    <PaginatedDropdown
                      placeholder={!props.readOnly && 'Odaberi zgradu'}
                      onItemSelect={onBuildingSelected}
                      selected={formik.values.building}
                      labelKey={'value'}
                      options={buildingOptionsLoading ? [] : buildingOptions}
                      disabled={props.readOnly}
                      error={!props.readOnly && formik.errors.building ? `${formik.errors.building}` : ''}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Zgrada - IBZ'} />
                    <Input disabled value={formik.values.ibz ?? ''} />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    {codebookField('buildingPart', 'Dio zgrade', selected => onTripletFieldSelected('buildingPart', selected))}
                  </Col>
                  <Col md={6}>
                    {codebookField(
                      'specialBuildingPart',
                      'Posebni dio zgrade (stan, poslovni prostor i sl.)',
                      selected => onTripletFieldSelected('specialBuildingPart', selected),
                      notUnique ? 'Posebni dio zgrade nije jedinstven za odabrani dio zgrade!' : '',
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>{codebookField('spaceType', 'Posebni dio zgrade - vrsta')}</Col>
                  <Col md={6}>
                    <Label labelText={'Korisna neto površina KNP [m2]'} />
                    <NumericFormat
                      decimalScale={2}
                      allowNegative={false}
                      thousandSeparator="."
                      decimalSeparator=","
                      value={formik.values.usefulNetArea ?? ''}
                      customInput={Input}
                      disabled={props.readOnly}
                      onBlur={() => formik.setFieldTouched('usefulNetArea', true)}
                      onValueChange={values => formik.setFieldValue('usefulNetArea', values.value === '' ? null : values.value)}
                      error={!props.readOnly && formik.errors.usefulNetArea ? `${formik.errors.usefulNetArea}` : ''}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>{codebookField('floor', 'Etaža (etaža ulaza)')}</Col>
                  <Col md={6}>{codebookField('floorType', 'Etaža - razina')}</Col>
                </Row>

                <div className={'form-header'}>Podaci o posebnom dijelu zgrade – prostorije</div>
                <Row>
                  <Col md={6}>{codebookField('roomCount', 'Broj soba (bez dnevnih boravaka)')}</Col>
                  <Col md={6}>{codebookField('livingRoomCount', 'Broj dnevnih boravaka')}</Col>
                </Row>
                <Row>
                  <Col md={6}>{codebookField('kitchenCount', 'Broj kuhinja')}</Col>
                  <Col md={6}>{codebookField('bathroomCount', 'Broj kupaonica')}</Col>
                </Row>
                <Row>
                  <Col md={6}>{codebookField('separateToiletCount', 'Broj zasebnih zahoda')}</Col>
                  <Col md={6}>{codebookField('disabilityAdaptation', 'Prilagodba invalidima')}</Col>
                </Row>
                <Row>
                  <Col md={6}>{codebookField('elevatorAccess', 'Pristup dizala')}</Col>
                </Row>

                <div className={'form-header'}>Podaci o posebnom dijelu zgrade – priključenje</div>
                <Row>
                  <Col md={6}>{codebookField('waterSupplyConnection', 'Priključenje na vodovod')}</Col>
                  <Col md={6}>{codebookField('sewageConnection', 'Priključenje na odvodnju')}</Col>
                </Row>
                <Row>
                  <Col md={6}>{codebookField('electricityConnection', 'Priključenje na električnu energiju')}</Col>
                  <Col md={6}>{codebookField('gasConnection', 'Priključenje na plinsku mrežu')}</Col>
                </Row>
                <Row>
                  <Col md={6}>{codebookField('internetConnection', 'Priključenje na internet')}</Col>
                </Row>

                <div className={'form-header'}>Podaci o posebnom dijelu zgrade – grijanje i hlađenje</div>
                <Row>
                  <Col md={6}>{codebookField('heatingConnection', 'Grijanje prostora')}</Col>
                  <Col md={6}>{codebookField('heatingEnergySource', 'Energent za grijanje prostora')}</Col>
                </Row>
                <Row>
                  <Col md={6}>{codebookField('coolingConnection', 'Hlađenje prostora')}</Col>
                </Row>

                <div className={'form-header'}>Podaci o posebnom dijelu zgrade - ostalo</div>
                <Row>
                  <Col md={12}>
                    <Label labelText={'Napomena'} />
                    <TextArea
                      disabled={props.readOnly}
                      value={formik.values.note ?? ''}
                      onChange={event => formik.setFieldValue('note', event.target.value)}
                    />
                  </Col>
                </Row>
              </div>
            </FormikProvider>
          </ModalBody>
          <ModalFooter>
            {!props.readOnly && (
              <>
                <Button label={'Odustani'} outline size={'lg'} onClick={closeModal} />
                <Button
                  label={'Spremi'}
                  size={'lg'}
                  isLoading={savePending}
                  disabled={!formik.dirty || !formik.isValid || notUnique || checkingUniqueness}
                  color={'info'}
                  onClick={() => formik.handleSubmit()}
                />
              </>
            )}
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default SpecialBuildingPartForm;
